import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withTheme } from 'styled-components'
import { Tabs } from 'antd'
import moment from 'moment'

// Components
import { Tag, Text } from '../../../Reusable/Text'
import { Image } from '../../../Reusable/Image'
import { ImageButton, TextButton } from '../../../Reusable/Button'
import { Flex } from '../../../Reusable/Container'
import { LabeledInput } from '../../../Reusable/Input'
import { ErrorAlert } from '../../../Reusable/Alert'
import TableView from '../../../Reusable/TableView'
import AlertModal from '../../../Reusable/AlertModal'
import { InitialCircle } from '../../../Reusable/InitialCircle'

// API and Utilities
import environment from '../../../../environment'
import { apiPOSTReq, getPreSignedS3Url } from '../../../../Utils/api'
import { toCurrency, capitalizeFirstLetter, getLogoFromDomain, capitalizeWords } from '../../../../Utils/util'

// Icons
import Search from '../../../../Images/search.png'
import More from "../../../../Images/more.png"

import { Dropdown, Menu } from '../../../Reusable/Dropdown'
import { FlexColumn } from '../../../Reusable/Container'
import hexToRgba from 'hex-to-rgba'

const { TabPane } = Tabs;

const defaultPagination = {
    current: 1,
    pageSize: 50,
    hideOnSinglePage: true
}

class InboxTable extends Component {
    state = {
        loading: false,
        selectedTab: this.props.selectedTab || "New",
        pagination: defaultPagination,
        inboxItems: [],
        showArchive: false,
        showUnArchive: false,
        selectedInboxItem: null,
        archiveLoading: false,
        searchLoading: false,
        initialLoadComplete: false,
        isInitialLoad: true,
        sortFieldName: "CreatedDate",
        sortDirection: "DESC"
    }

    componentDidMount() {
        this.fetchInbox({ selectedTab: this.state.selectedTab, pagination: this.state.pagination })
    }

    componentDidUpdate(prevProps) {
        // If selectedTab prop changes, update state and fetch new data
        if (prevProps.selectedTab !== this.props.selectedTab) {
            this.setState({ selectedTab: this.props.selectedTab }, () => {
                this.fetchInbox({ selectedTab: this.props.selectedTab, pagination: defaultPagination })
            })
        }
        
        // If vendorId prop changes, reload data
        if (prevProps.vendorId !== this.props.vendorId) {
            this.fetchInbox({ selectedTab: this.state.selectedTab, pagination: this.state.pagination })
        }
    }

    onSearch = (value) => {
        this.setState({ searchLoading: true, isInitialLoad: false })
        this.fetchInbox({ searchStr: value, selectedTab: this.state.selectedTab, pagination: defaultPagination })
    }

    handleSearchTextChange = (event) => {
        const target = event.target
        this.onSearch(target.value)
    }

    delay = ms => new Promise(res => setTimeout(res, ms))

    checkForProcessingBills = async () => {
        const { inboxItems, selectedTab, pagination } = this.state
        const processingInboxItems = inboxItems.filter(inboxItem => (!inboxItem.parsedInvoiceId && !inboxItem.processingError))
        
        if (processingInboxItems.length > 0 && (selectedTab === "New" || selectedTab === "Inbox")) {
            // Wait for 5s before fetching the inbox again
            await this.delay(5000)
            this.setState({ isInitialLoad: false })
            this.fetchInbox({ selectedTab, pagination })
        }
    }

    fetchInbox = (options) => {
        const { searchStr, selectedTab, pagination, sortFieldName, sortDirection } = options || {}
        const { vendorId } = this.props
        
        var body = {
            size: pagination.pageSize,
            page: pagination.current - 1,
            sortDirection: "DESC",
            viewBy: "FILTER_STATUS",
            processingError: false,
        }

        if (selectedTab === "Archive") {
            body["converted"] = false
            body["archived"] = true
        } else if (selectedTab === "Inbox") {
            body["archived"] = false
        } else if (selectedTab === "New") {
            body["converted"] = false
            body["archived"] = false
        } else if (selectedTab === "Processed") {
            body["converted"] = true
            body["archived"] = false
        }

        // Add vendorId to the request if provided
        if (vendorId) {
            body["vendorId"] = vendorId
        }

        body.sortFieldName = sortFieldName;
        body.sortDirection = sortDirection;

        if (searchStr) body["searchStr"] = searchStr
        
        this.setState({ loading: true })
        apiPOSTReq(`${environment.payBaseUrl}/payables/bills/getBillInboxItems`, { "BankProvider": environment.bankProvider.crb }, body, (err, resp) => {
            try {
                const data = resp || {}
                if (data.result) {
                    this.setState({
                        inboxItems: data.billInboxItems || [],
                        pagination: {
                            ...pagination,
                            total: data.count
                        },
                        initialLoadComplete: true,
                        sortFieldName: body.sortFieldName,
                        sortDirection: body.sortDirection
                    })
                    this.checkForProcessingBills();
                } else {
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later")
                }
            } catch (error) {
                console.log("ERRR /payables/bills/getBillInboxItems", error, err, resp)
                ErrorAlert({ description: error.message })
                this.setState({ initialLoadComplete: true })
            } finally {
                this.setState({ loading: false, searchLoading: false })
            }
        })
    }

    archiveInboxItem = (event, inboxItem) => {
        this.setState({ archiveLoading: true, isInitialLoad: false })
        apiPOSTReq(`${environment.payBaseUrl}/payables/bills/archiveBillInboxItems`, {}, { idList: [inboxItem.id] }, (err, resp) => {
            try {
                this.setState({ archiveLoading: false, showArchive: false, showUnArchive: false, selectedInboxItem: null })
                const data = resp || {}
                if (data.result) {
                    this.fetchInbox({ selectedTab: this.state.selectedTab, pagination: this.state.pagination })
                } else {
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later")
                }
            } catch (error) {
                console.log("ERRR /payables/bills/archiveBillInboxItems", error, err, resp)
                ErrorAlert({ description: error.message })
            }
        })
        // stops the click event from bubbling up to the parent component 
        event.stopPropagation()
    }

    unarchiveInboxItem = (event, inboxItem) => {
        this.setState({ archiveLoading: true, isInitialLoad: false })
        apiPOSTReq(`${environment.payBaseUrl}/payables/bills/unarchiveBillInboxItems`, {}, { idList: [inboxItem.id] }, (err, resp) => {
            try {
                this.setState({ archiveLoading: false, showArchive: false, showUnArchive: false, selectedInboxItem: null })
                const data = resp || {}
                if (data.result) {
                    this.fetchInbox({ selectedTab: this.state.selectedTab, pagination: this.state.pagination })
                } else {
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later")
                }
            } catch (error) {
                console.log("ERRR /payables/bills/unarchiveBillInboxItems", error, err, resp)
                ErrorAlert({ description: error.message })
            }
        })
        // stops the click event from bubbling up to the parent component 
        event.stopPropagation()
    }

    handleTableChange = (pagination, filters, sorter) => {
        sorter = sorter || {};
        var sortFieldName = null;
        var sortDirection = null;

        if(sorter.order) {
            if(sorter.order === 'ascend') sortDirection = 'ASC';
            else sortDirection = 'DESC';

            if(sorter.field) {
                switch(sorter.field) {
                    case "from":
                        sortFieldName = "vendorName";
                        break;
                    default:
                        sortFieldName = sorter.field;
                }

                sortFieldName = capitalizeFirstLetter(sortFieldName); // To convert to PascalCase
            }
        }
        
        // Mark as no longer the initial load
        this.setState({ isInitialLoad: false });
        this.fetchInbox({ pagination: pagination, selectedTab: this.state.selectedTab, sortFieldName, sortDirection });
    }

    handleBillDetail = (inboxItem) => {
        if (this.props.onBillSelect) {
            // Use custom handler if provided
            this.props.onBillSelect(inboxItem);
        } else if (this.props.history) {
            // Default behavior: navigate to detail page
            this.props.history.push({
                pathname: '/payables/inbox/detail',
                state: { inboxItem: inboxItem }
            });
        }
    }

    getColumns = (tab) => {
        const { theme, hideVendorColumn } = this.props
        var columns = []
        
        // Only add the From/Vendor column if not hidden
        if (!hideVendorColumn) {
            columns.push({
                title: 'From',
                dataIndex: 'from',
                key: 'from',
                sorter: true,
                render: (from, inboxItem) => {
                    var split = (from || "").split("<");
                    var name = inboxItem.vendorName || split[0];
                    return (
                        <Flex start centerHorizontally gap='8px'>
                            <div>
                                <InitialCircle size={30} name={name} useFirstWordOnly={true}  />
                            </div>
                            
                            <FlexColumn start gap='2px'>
                                <Flex start centerH gap='8px'>
                                    <Text weight={500} color={theme.colors.primary2}>{capitalizeFirstLetter(name)}</Text>
                                    {
                                        !inboxItem.parsedInvoiceId && !inboxItem.processingError &&
                                        <Tag title='Please wait for a few minutes while we process your bill'>Processing</Tag>
                                    }
                                </Flex>
                                
                            
                                <Text size={'14px'} height={'14px'} weight={500} color={hexToRgba(this.props.theme.colors.primary2, 0.5)}>
                                    {inboxItem.subject}
                                </Text>
                            </FlexColumn>
                        </Flex>
                    )
                }
            })
        }
        
        if (hideVendorColumn) {
            columns.push({
                title: 'Subject',
                dataIndex: 'subject',
                key: 'Subject',
                sorter: true,
                showSorterTooltip: false,
            })
        }
        

        if (tab === "Inbox" || tab === "New") {
            columns.push({
                title: 'Amount',
                dataIndex: 'invoiceAmount',
                key: 'invoiceAmount',
                render: invoiceAmount => "$" + toCurrency(invoiceAmount)
            })
        }

        columns.push({
            title: 'Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            sorter: true,
            showSorterTooltip: false,
            // width: "170px",
            align: 'right',
            render: (createdDate, record) => {
                const localTZ = moment.tz.guess()
                return moment.utc(createdDate).tz(localTZ).format("MMM D \'YY, h:mm A")
                // (
                //     <FlexColumn right gap='2px'>
                //         <Text size={'14px'} weight={500} color={theme.colors.primary2}>${toCurrency(record.invoiceAmount)}</Text>
                //         <Text size={'14px'} height={'14px'} weight={500} color={hexToRgba(this.props.theme.colors.primary2, 0.5)}>
                //             {moment.utc(createdDate).tz(localTZ).format("MMM D \'YY, h:mm A")}
                //         </Text>
                //     </FlexColumn>
                // )
            },
        })
        
        if (tab === "Inbox" || tab === "New" || tab === "Processed" || tab === "Archive") {
            columns.push({
                title: '',
                key: 'action',
                width: "40px",
                onHover: true,
                render: (inboxItem) => {
                    return (
                        <Dropdown
                            placement='bottomRight'
                            trigger='click'
                            key={`${inboxItem.id}-Dropdown`}
                            overlay={
                                <Menu style={{ padding: 24, borderRadius: 8, zIndex: 1001 }} key={`${inboxItem.id}-Menu`} onClick={() => { }}>
                                    <FlexColumn gap='24px'>
                                        {
                                            (tab === "Inbox" || tab === "New" || tab === "Processed") && !(inboxItem.converted || inboxItem.archived) &&
                                            <TextButton text='ARCHIVE' onClick={(e) => {
                                                e.stopPropagation();
                                                this.setState({ showArchive: true, selectedInboxItem: inboxItem })}} 
                                            />
                                        }
                                        {
                                            (tab === "Archive") &&
                                            <TextButton text='UNARCHIVE' onClick={(e) => {
                                                e.stopPropagation();
                                                this.setState({ showUnArchive: true, selectedInboxItem: inboxItem })}} 
                                            />
                                        }
                                        {
                                            (tab === "Archive") &&
                                            <TextButton text='VIEW ATTACHMENT' 
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    getPreSignedS3Url({ url: (inboxItem.attachmentUrl || {}).uri }, (err, preSignedUrl) => {
                                                        window.open(preSignedUrl, '_blank')
                                                    })
                                                }}
                                            />
                                        }
                                    </FlexColumn>
                                </Menu>
                            }
                        >
                            <ImageButton src={More} />
                        </Dropdown>
                    )
                }
            })
        }
        
        return columns
    }

    render() {
        const { 
            selectedTab, 
            inboxItems, 
            pagination, 
            loading, 
            showArchive, 
            showUnArchive, 
            selectedInboxItem, 
            archiveLoading,
            searchLoading,
            initialLoadComplete,
            isInitialLoad, 
            sortFieldName, 
            sortDirection
        } = this.state
        
        const { 
            title = "Inbox", 
            description = "Bills sent to your AP email will show up right here.", 
            showSearch = true,
            customPlaceholder,
            customDescription,
            showTabs = false,
            onTabChange,
            hideOnEmpty
        } = this.props
        
        // If hideOnEmpty is true and it's the initial load with no data, return null
        if (hideOnEmpty && initialLoadComplete && inboxItems.length === 0 && isInitialLoad) {
            return null
        }
        
        let tableTitle = title
        let descText = description
        
        if (selectedTab === "Archive") {
            tableTitle = this.props.archiveTitle || "Bills"
            descText = this.props.archiveDescription || "Archived bills from your Inbox. Attachments other than bills are archived automatically."
        }
        
        const inboxTable = (
            <TableView
                id="inbox-table"
                titleText={tableTitle}
                descText={customDescription || descText}
                tableLayout='auto'
                columns={this.getColumns(selectedTab)}
                dataSource={inboxItems}
                rowKey='id'
                pagination={pagination}
                loading={loading || searchLoading}
                onChange={this.handleTableChange}
                bottomDivider
                sortFieldName={sortFieldName}
                sortDirection={sortDirection}
                ctaContent={
                    showSearch &&
                    <LabeledInput
                        nomargin
                        id="searchTerm"
                        key="searchTerm"
                        placeholder={customPlaceholder || "Search by Sender and Subject"}
                        prefixIcon={<Image width='12px' height='12px' src={Search} />}
                        onChange={this.handleSearchTextChange}
                        width='270px'
                    />
                }
                onRow={selectedTab === "New" ? (record, rowIndex) => {
                    return {
                        onClick: event => {
                            record.parsedInvoiceId ?
                                this.handleBillDetail(record)
                                :
                                ErrorAlert({ title: "Processing", description: "This bill is being processed by our AI Engine, check back shortly." })
                        },
                    }
                }
                    :
                    null
                }
            />
        )
        
        return (
            <>
                {showTabs ? (
                    <Tabs
                        defaultActiveKey={selectedTab}
                        onChange={(tabKey) => {
                            this.setState({ isInitialLoad: false });
                            this.onChangeTab(tabKey)
                            if (onTabChange) onTabChange(tabKey)
                        }}
                        style={{ marginBottom: 12 }}
                    >
                        <TabPane tab="New" key="New">
                            {inboxTable}
                        </TabPane>
                        <TabPane tab="Processed" key="Processed">
                            {inboxTable}
                        </TabPane>
                        <TabPane tab="Archived" key="Archive">
                            {inboxTable}
                        </TabPane>
                    </Tabs>
                ) : (
                    inboxTable
                )}

                <AlertModal
                    question
                    visible={showArchive || showUnArchive}
                    loading={archiveLoading}
                    title={`${showArchive ? 'Archive' : 'Unarchive'} bill`}
                    description={`Are you sure you want to ${showArchive ? 'archive' : 'unarchive'} this bill?`}
                    buttonTitle={`${showArchive ? 'ARCHIVE' : 'UNARCHIVE'}`}
                    cancelButtonText='CANCEL'
                    onConfirm={(e) => {
                        if(showArchive) {
                            this.archiveInboxItem(e, selectedInboxItem);
                        } else {
                            this.unarchiveInboxItem(e, selectedInboxItem)
                        }
                    }}
                    onCancel={() => this.setState({ showArchive: false, showUnArchive: false, selectedInboxItem: null })}
                />
            </>
        )
    }
}

InboxTable.defaultProps = {
    selectedTab: "New",
    showSearch: true,
    hideVendorColumn: false,
    showTabs: false,
    hideOnEmpty: false
}

function mapStateToProps(state) {
    return {
        aionStore: state.aionAppReducer
    }
}

function mapDispatchToProps(dispatch) {
    return {
        dispatch
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(InboxTable))